import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  DEFAULT_SEQUENCE,
  DEFAULT_SEND_HOUR,
  DEFAULT_SEND_MINUTE,
} from "../constants";
import {
  buildMergeVars,
  classNames,
  detectUnresolvedVars,
  formatStepCadence,
  renderTemplate,
} from "../workspace/utils";
import { getSignature } from "../../../utils/emailSignature";

const SAMPLE_LEAD = {
  first_name: "Alex",
  last_name: "Rivera",
  owner_name: "Alex Rivera",
  company_name: "Harbor View Rentals",
  website: "harborviewrentals.com",
  phone: "(555) 123-9087",
  city: "San Diego",
  state: "CA",
  title: "Owner",
};

const SAMPLE_CAMPAIGN_LEAD = { stepIndex: 0 };

const DEFAULT_UNSUBSCRIBE_FOOTER =
  'If you\'d rather not hear from me, just reply "stop" and I\'ll take you off.';

function emptyStep(stepIndex) {
  return {
    stepIndex,
    name: `Step ${stepIndex + 1}`,
    subject: "",
    body: "",
    delayDays: stepIndex === 0 ? 0 : 3,
    sendAtHour: DEFAULT_SEND_HOUR,
    sendAtMinute: DEFAULT_SEND_MINUTE,
    businessDaysOnly: true,
    enabled: true,
  };
}

function parseTimeValue(value) {
  if (typeof value !== "string") return { hour: DEFAULT_SEND_HOUR, minute: DEFAULT_SEND_MINUTE };
  const [h, m] = value.split(":");
  const hour = Math.min(Math.max(Number(h), 0), 23);
  const minute = Math.min(Math.max(Number(m), 0), 59);
  return {
    hour: Number.isFinite(hour) ? hour : DEFAULT_SEND_HOUR,
    minute: Number.isFinite(minute) ? minute : DEFAULT_SEND_MINUTE,
  };
}

function timeValueFromStep(step) {
  const h = Number.isFinite(step?.sendAtHour) ? step.sendAtHour : DEFAULT_SEND_HOUR;
  const m = Number.isFinite(step?.sendAtMinute) ? step.sendAtMinute : DEFAULT_SEND_MINUTE;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export default function CampaignSettingsModal({
  isOpen,
  onClose,
  campaign,
  adminInfo,
  onSave,
}) {
  const reduxAdmin = useSelector((s) => s.login?.adminInfo || {});
  const effectiveAdmin = adminInfo || reduxAdmin;
  const ownerEmail =
    effectiveAdmin?.gmailPrimaryAddress || effectiveAdmin?.email || "";

  const [form, setForm] = useState(() => normalizeForm(campaign));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [signatureHtml, setSignatureHtml] = useState(() => getSignature(ownerEmail));
  const bodyRefs = useRef([]);

  useEffect(() => {
    if (!isOpen) return;
    setSignatureHtml(getSignature(ownerEmail));
  }, [isOpen, ownerEmail]);

  const sampleVars = useMemo(
    () =>
      buildMergeVars({
        lead: SAMPLE_LEAD,
        campaignLead: SAMPLE_CAMPAIGN_LEAD,
        admin: effectiveAdmin,
      }),
    [effectiveAdmin]
  );

  useEffect(() => {
    if (!isOpen) return;
    setForm(normalizeForm(campaign));
    setError("");
  }, [isOpen, campaign]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  if (!isOpen || !campaign) return null;

  const updateStep = (index, patch) => {
    setForm((current) => ({
      ...current,
      sequenceSteps: current.sequenceSteps.map((step, i) =>
        i === index ? { ...step, ...patch } : step
      ),
    }));
  };

  const removeStep = (index) => {
    setForm((current) => ({
      ...current,
      sequenceSteps: current.sequenceSteps
        .filter((_, i) => i !== index)
        .map((step, i) => ({ ...step, stepIndex: i })),
    }));
  };

  const addStep = () => {
    setForm((current) => ({
      ...current,
      sequenceSteps: [...current.sequenceSteps, emptyStep(current.sequenceSteps.length)],
    }));
  };

  const insertSignatureIntoStep = (index) => {
    if (!signatureHtml) return;
    setForm((current) => ({
      ...current,
      sequenceSteps: current.sequenceSteps.map((step, i) => {
        if (i !== index) return step;
        const existing = (step.body || "").trim();
        if (existing.includes(signatureHtml.trim())) return step;
        const nextBody = existing
          ? `${existing}\n<br/><br/>\n${signatureHtml}`
          : signatureHtml;
        return { ...step, body: nextBody };
      }),
    }));
  };

  const save = async () => {
    if (!form.name.trim()) {
      setError("Campaign name is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave?.({
        name: form.name.trim(),
        description: form.description.trim(),
        status: form.status,
        senderAlias: form.senderAlias.trim(),
        unsubscribeFooter: (form.unsubscribeFooter || "").trim(),
        disableAutoSignature: true,
        sequenceSteps: form.sequenceSteps.map((step, i) => ({
          stepIndex: i,
          name: step.name,
          subject: step.subject,
          body: step.body,
          delayDays: Number.isFinite(step.delayDays) ? step.delayDays : 0,
          sendAtHour: Number.isFinite(step.sendAtHour)
            ? step.sendAtHour
            : DEFAULT_SEND_HOUR,
          sendAtMinute: Number.isFinite(step.sendAtMinute)
            ? step.sendAtMinute
            : DEFAULT_SEND_MINUTE,
          businessDaysOnly: step.businessDaysOnly !== false,
          enabled: step.enabled !== false,
        })),
      });
      onClose?.();
    } catch (err) {
      console.error("Failed to save campaign", err);
      setError(err?.response?.data?.error || "Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-stretch justify-center bg-zinc-900/40 backdrop-blur-sm p-4 md:p-6">
      <div className="flex w-full max-w-[1040px] flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-panel">
        <header className="flex items-center justify-between border-b border-zinc-200 px-6 py-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
              Campaign settings
            </p>
            <h2 className="text-base font-semibold text-zinc-900">{campaign.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-md px-2.5 py-1.5 text-sm font-medium text-zinc-600 hover:bg-zinc-100"
          >
            Close
          </button>
        </header>

        <div className="grid flex-1 grid-cols-[320px,1fr] overflow-hidden">
          <aside className="overflow-y-auto border-r border-zinc-200 bg-zinc-50/60 p-5">
            <div className="space-y-4">
              <Field label="Name" required>
                <input
                  value={form.name}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, name: event.target.value }))
                  }
                  className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
              </Field>
              <Field label="Description">
                <textarea
                  value={form.description}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, description: event.target.value }))
                  }
                  rows={3}
                  className="w-full resize-none rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
              </Field>
              <Field label="Status">
                <select
                  value={form.status}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, status: event.target.value }))
                  }
                  className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </Field>
              <Field
                label="Unsubscribe line"
                hint="Shown at the bottom. Empty = don't append."
              >
                <textarea
                  rows={2}
                  value={form.unsubscribeFooter}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      unsubscribeFooter: event.target.value,
                    }))
                  }
                  placeholder={DEFAULT_UNSUBSCRIBE_FOOTER}
                  className="w-full resize-none rounded-lg border border-zinc-300 bg-white px-3 py-2 text-xs focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
              </Field>
              <Field label="Preferred from alias" hint={effectiveAdmin?.gmailPrimaryAddress || effectiveAdmin?.email}>
                <input
                  value={form.senderAlias}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, senderAlias: event.target.value }))
                  }
                  placeholder={effectiveAdmin?.gmailPrimaryAddress || "sender@domain.com"}
                  className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
              </Field>

              <div className="rounded-xl border border-zinc-200 bg-white p-3 text-xs leading-5 text-zinc-600">
                <p className="font-semibold text-zinc-900">How timing works</p>
                <p className="mt-1">
                  Each step schedules from the previous step — <span className="font-medium text-zinc-800">+N days</span> at the <span className="font-medium text-zinc-800">Send at</span> time. Turn on <span className="font-medium text-zinc-800">Skip weekends</span> to count business days only and roll sends that land on Sat/Sun forward to Monday.
                </p>
              </div>

              <div className="rounded-xl border border-zinc-200 bg-white p-3 text-xs leading-5 text-zinc-600">
                <p className="font-semibold text-zinc-900">Tracking is off by design</p>
                <p className="mt-1">
                  We no longer track opens or clicks on outbound email. The only engagement signal is replies synced from Gmail. Unsubscribe remains enabled for compliance.
                </p>
              </div>
            </div>
          </aside>

          <div className="flex flex-col overflow-hidden">
            <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  Sequence
                </p>
                <p className="text-sm text-zinc-600">
                  Steps run in order for every enrolled lead. Use template variables like{" "}
                  <code className="rounded bg-zinc-100 px-1 py-0.5 text-xs">{`{{company_name}}`}</code>
                  .
                </p>
              </div>
              <button
                onClick={addStep}
                className="rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
              >
                + Add step
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-3">
                {form.sequenceSteps.map((step, index) => (
                  <div
                    key={index}
                    className={classNames(
                      "rounded-xl border bg-white p-4 shadow-card",
                      step.enabled === false ? "border-zinc-200 opacity-70" : "border-zinc-200"
                    )}
                  >
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-indigo-600 px-1.5 text-[11px] font-semibold text-white">
                          {index + 1}
                        </span>
                        <span className="text-xs font-semibold text-zinc-900">
                          {formatStepCadence(step, { isFirst: index === 0 })}
                        </span>
                        {index === 0 ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                            On enroll
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="inline-flex items-center gap-1.5 text-xs text-zinc-700">
                          <input
                            type="checkbox"
                            checked={step.enabled !== false}
                            onChange={(event) => updateStep(index, { enabled: event.target.checked })}
                            className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600"
                          />
                          Enabled
                        </label>
                        <button
                          onClick={() => removeStep(index)}
                          disabled={form.sequenceSteps.length <= 1}
                          title="Remove step"
                          className="rounded-md px-2 py-1 text-xs font-medium text-zinc-500 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:text-zinc-300"
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-[1fr,110px,130px]">
                      <Field label="Step name">
                        <input
                          value={step.name}
                          onChange={(event) => updateStep(index, { name: event.target.value })}
                          className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                        />
                      </Field>
                      <Field
                        label="Delay (days)"
                        hint={index === 0 ? "0 = on enroll" : null}
                      >
                        <input
                          type="number"
                          min="0"
                          value={step.delayDays}
                          onChange={(event) =>
                            updateStep(index, { delayDays: Number(event.target.value || 0) })
                          }
                          className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                        />
                      </Field>
                      <Field label="Send at">
                        <input
                          type="time"
                          value={timeValueFromStep(step)}
                          onChange={(event) => {
                            const { hour, minute } = parseTimeValue(event.target.value);
                            updateStep(index, { sendAtHour: hour, sendAtMinute: minute });
                          }}
                          className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                        />
                      </Field>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg bg-zinc-50 px-3 py-2 text-xs text-zinc-700">
                      <label className="inline-flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={step.businessDaysOnly !== false}
                          onChange={(event) =>
                            updateStep(index, { businessDaysOnly: event.target.checked })
                          }
                          className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600"
                        />
                        <span>Skip weekends</span>
                      </label>
                      <span className="text-zinc-300">·</span>
                      <span className="text-zinc-500">
                        Sends {formatStepCadence(step, { isFirst: index === 0 }).toLowerCase()}
                        {index === 0 ? " after the lead is enrolled." : " after the previous step."}
                      </span>
                    </div>

                    <Field label="Subject" containerClassName="mt-3">
                      <input
                        value={step.subject}
                        onChange={(event) => updateStep(index, { subject: event.target.value })}
                        className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                      />
                    </Field>

                    <div className="mt-3">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-zinc-700">Body (HTML allowed)</label>
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => insertSignatureIntoStep(index)}
                            disabled={!signatureHtml}
                            title={
                              signatureHtml
                                ? "Insert your saved signature into this step body"
                                : "Save a signature in Settings → Email signature first"
                            }
                            className="rounded-md border border-zinc-300 bg-white px-2 py-0.5 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Insert signature
                          </button>
                          <button
                            type="button"
                            onClick={() => setActiveStep(activeStep === index ? -1 : index)}
                            className="rounded-md border border-zinc-300 bg-white px-2 py-0.5 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50"
                          >
                            {activeStep === index ? "Hide preview" : "Preview"}
                          </button>
                        </div>
                      </div>
                      <textarea
                        rows={5}
                        ref={(el) => (bodyRefs.current[index] = el)}
                        value={step.body}
                        onChange={(event) => updateStep(index, { body: event.target.value })}
                        className="mt-1 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 font-mono text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                      />
                      <StepTemplateHealth step={step} sampleVars={sampleVars} />
                      {activeStep === index ? (
                        <StepPreview step={step} sampleVars={sampleVars} />
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer className="flex items-center justify-between border-t border-zinc-200 bg-white px-6 py-3">
          <div className="text-xs text-zinc-500">
            {error ? <span className="text-red-600">{error}</span> : "Changes apply to future queue items."}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-zinc-300"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function StepTemplateHealth({ step, sampleVars }) {
  const unresolvedSubject = detectUnresolvedVars(step.subject, sampleVars);
  const unresolvedBody = detectUnresolvedVars(step.body, sampleVars);
  const unresolved = Array.from(new Set([...unresolvedSubject, ...unresolvedBody]));
  if (!unresolved.length) return null;
  return (
    <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] leading-4 text-amber-900">
      <span className="font-semibold">Needs a fallback:</span>{" "}
      {unresolved.map((key, i) => (
        <code key={key} className="rounded bg-amber-100 px-1 py-0.5 font-mono">
          {`{{${key}}}`}{i < unresolved.length - 1 ? " " : ""}
        </code>
      ))}
      . Use <code className="rounded bg-white px-1 py-0.5 font-mono">{`{{${unresolved[0]}|friend}}`}</code> so empty values fall back to something natural.
    </div>
  );
}

function StepPreview({ step, sampleVars }) {
  const subject = renderTemplate(step.subject || "", sampleVars);
  const body = renderTemplate(step.body || "", sampleVars);
  return (
    <div className="mt-3 overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50/60">
      <div className="border-b border-zinc-200 bg-white px-3 py-2 text-xs">
        <span className="mr-1 font-semibold text-zinc-700">Subject:</span>
        <span className="text-zinc-900">{subject || <em className="text-zinc-400">(empty)</em>}</span>
      </div>
      <div className="bg-white px-3 py-2 text-xs text-zinc-500">
        Preview for <span className="font-medium text-zinc-700">Alex @ Harbor View Rentals</span>
      </div>
      <div
        className="prose prose-sm max-w-none bg-white px-3 pb-3 text-sm text-zinc-800"
        dangerouslySetInnerHTML={{ __html: body || '<p class="text-zinc-400"><em>Empty body</em></p>' }}
      />
    </div>
  );
}

function Field({ label, required, hint, children, containerClassName = "" }) {
  return (
    <div className={containerClassName}>
      <label className="flex items-center justify-between text-xs font-medium text-zinc-700">
        <span>
          {label}
          {required ? <span className="ml-1 text-red-500">*</span> : null}
        </span>
        {hint ? <span className="text-[10px] text-zinc-400">{hint}</span> : null}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function normalizeForm(campaign) {
  return {
    name: campaign?.name || "",
    description: campaign?.description || "",
    status: campaign?.status || "active",
    senderAlias: campaign?.senderAlias || "",
    unsubscribeFooter:
      campaign?.unsubscribeFooter === undefined
        ? DEFAULT_UNSUBSCRIBE_FOOTER
        : campaign.unsubscribeFooter,
    sequenceSteps:
      campaign?.sequenceSteps?.length
        ? campaign.sequenceSteps.map((step, i) => ({
            stepIndex: i,
            name: step.name || `Step ${i + 1}`,
            subject: step.subject || "",
            body: step.body || "",
            delayDays: step.delayDays ?? (i === 0 ? 0 : 3),
            sendAtHour: Number.isFinite(step.sendAtHour)
              ? step.sendAtHour
              : DEFAULT_SEND_HOUR,
            sendAtMinute: Number.isFinite(step.sendAtMinute)
              ? step.sendAtMinute
              : DEFAULT_SEND_MINUTE,
            businessDaysOnly: step.businessDaysOnly !== false,
            enabled: step.enabled !== false,
          }))
        : DEFAULT_SEQUENCE,
  };
}
